import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useSlotBlocks, useSlotCapacity, usePublicAppSettings, useEffectiveSlotConfig } from "@/lib/queries";
import { isSlotBlocked, dayUnavailableReason } from "@emr/shared/scheduling";
import { DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { todayIST } from "@emr/shared/india";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import type { AppointmentRow } from "@/lib/queries";
import { capFor } from "./slotCapacity";

/**
 * BULK reschedule dialog (2026-10-31) — admin-only bulk action from the
 * Pending Confirmations panel.
 *
 * Applies ONE target (date + slot + status) to every selected pending row,
 * calling the existing per-row `POST /appointments/:id/rebook` endpoint
 * SEQUENTIALLY so each row runs the full server-side guard set (working-day,
 * blocks, slot-key, capacity, same-day duplicate). Per-row failures are
 * collected (never abort the whole batch) and reported in a summary toast;
 * the same per-row payload shape as `RebookDialog` is used.
 */
export function BulkRebookDialog({
  open,
  onOpenChange,
  rows,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Selected pending rows to reschedule (admin-verified by the caller). */
  rows: AppointmentRow[];
  /** Clears the panel's checkbox selection after a completed batch. */
  onDone?: () => void;
}) {
  const { data: blocksData } = useSlotBlocks();
  const { data: settings } = usePublicAppSettings();
  const blocks = blocksData?.rows ?? [];
  // Invalidate the appointments + dashboard caches after the batch so the
  // panel refetches (mirrors useRebookAppointment's onSuccess invalidation).
  const qc = useQueryClient();

  // One shared target — same shape as RebookDialog's single-row target.
  const [newStatus, setNewStatus] = useState<"Pending" | "Booked">("Pending");
  const [newDate, setNewDate] = useState(todayIST());
  const [newSlot, setNewSlot] = useState<string | null>(null);
  const [newArrival, setNewArrival] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset the shared target each time the dialog opens.
  useEffect(() => {
    if (open) {
      setNewStatus("Pending");
      setNewDate(todayIST());
      setNewSlot(null);
      setNewArrival("");
    }
  }, [open]);

  const { data: capacity } = useSlotCapacity(newDate);
  const { slotList } = useEffectiveSlotConfig(newDate, settings);

  const targetDateObj = new Date(newDate + "T00:00:00");
  const weeklySchedule = (settings?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const unavailable = dayUnavailableReason(targetDateObj, weeklySchedule, blocks as any);

  // Per-key capacity: shared lookup (slotCapacity.ts) — same rule as the
  // other appointment dialogs (Phase 2 slots map first, legacy fallback).

  const submit = async () => {
    if (!rows.length || busy) return;
    if (!newDate) { toast.error("Please select a date"); return; }
    if (unavailable) {
      toast.error(unavailable.kind === "blocked" ? "Whole day is blocked for this date" : "Selected date is not a working day");
      return;
    }
    if (newStatus === "Booked" && !newSlot) {
      toast.error("Pick a slot for a confirmed appointment");
      return;
    }
    if (newSlot && isSlotBlocked(targetDateObj, newSlot, blocks as any)) {
      toast.error("Selected slot is blocked for this date");
      return;
    }
    // Per-row arrival: explicit time, else the slot def's startTime, else
    // date-only (the API derives the same defaults per row).
    let arrivalIso: string | undefined;
    if (newSlot) {
      const def = slotList.find((s) => s.key === newSlot);
      const time = newArrival || def?.startTime || "09:00";
      arrivalIso = new Date(`${newDate}T${time}:00`).toISOString();
    }

    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    // Sequential — each row's server-side guards re-read capacity/blocks
    // fresh, so rows don't race each other for the same slot capacity.
    for (const r of rows) {
      try {
        await apiFetch(`/appointments/${r.id}/rebook`, {
          method: "POST",
          body: JSON.stringify({
            newDate,
            newSlot: newSlot ?? null,
            newArrivalTime: arrivalIso,
            newStatus,
          }),
        });
        ok++;
      } catch {
        failed.push(`${r.patientName ?? r.id} (${r.clinicUhid ?? "—"})`);
      }
    }
    setBusy(false);
    if (ok > 0) {
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Rescheduled ${ok}/${rows.length} appointment${rows.length > 1 ? "s" : ""}`);
    }
    if (failed.length > 0) {
      toast.error(`Failed: ${failed.join(", ")}`);
    }
    onDone?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule {rows.length} selected</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-cream-100 p-3 text-sm dark:bg-dark-700">
            <div className="font-medium">One target for all selected pendings</div>
            <div className="text-xs text-muted">
              Each row is rescheduled individually — working-day, block, capacity and
              duplicate guards run per appointment server-side; failures are listed.
            </div>
          </div>
          <div>
            <Label>Reschedule as</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setNewStatus("Pending")}
                className={`rounded-lg border p-3 text-left text-sm transition ${newStatus === "Pending" ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"}`}
              >
                <div className="font-medium">Pending confirmation</div>
                <div className="text-xs text-muted">Holds the slot without confirming</div>
              </button>
              <button
                type="button"
                onClick={() => setNewStatus("Booked")}
                className={`rounded-lg border p-3 text-left text-sm transition ${newStatus === "Booked" ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"}`}
              >
                <div className="font-medium">Confirmed</div>
                <div className="text-xs text-muted">A booked appointment</div>
              </button>
            </div>
          </div>
          <div>
            <Label>New date</Label>
            <Input
              type="date"
              value={newDate}
              min={todayIST()}
              onChange={(e) => setNewDate(e.target.value)}
            />
            {newDate && unavailable && (
              <div className="mt-1 text-xs font-medium text-red-600">
                {unavailable.kind === "blocked"
                  ? `Whole day blocked${unavailable.reason ? ` — ${unavailable.reason}` : ""}`
                  : "Not a working day"}
              </div>
            )}
          </div>
          <div>
            <Label>Slot {newStatus === "Pending" && <span className="font-normal text-muted">(optional for pending)</span>}</Label>
            <div className="grid grid-cols-2 gap-2">
              {slotList.map((def) => {
                const blocked = isSlotBlocked(new Date(newDate + "T00:00:00"), def.key, blocks as any);
                const cap = capFor(capacity, def.key);
                const booked = cap?.booked ?? 0;
                const max = cap?.max ?? 0;
                const full = max > 0 && booked >= max;
                return (
                  <button
                    key={def.key}
                    type="button"
                    disabled={blocked}
                    onClick={() => { setNewSlot(def.key); setNewArrival(def.startTime); }}
                    className={`rounded-lg border p-3 text-left text-sm transition ${newSlot === def.key ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 hover:border-maroon-300 dark:border-dark-700"} ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <div className="font-medium">{def.label}</div>
                    <div className="text-xs text-muted">
                      {blocked ? "Blocked" : (
                        <>
                          <span className={full ? "font-semibold text-amber-700" : ""}>
                            {booked}{max > 0 ? `/${max}` : ""}
                          </span>
                          {full && <span className="ml-1 text-amber-700">· full</span>}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {newStatus === "Pending" && !newSlot && (
              <div className="mt-1 text-xs text-muted">
                No slot picked — pendings stay slot-less (date-only, like review-flow pendings).
              </div>
            )}
          </div>
          {newSlot && (
            <div>
              <Label>Arrival time</Label>
              <Input type="time" value={newArrival} onChange={(e) => setNewArrival(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!newDate || !!unavailable || busy} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            {busy ? "Rescheduling…" : `Reschedule ${rows.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}